export const servicesPage = {
  variant: "services",
  path: "/services/",
  seoTitle: "Services | Business Improvement, Automation & AI | LevelUp360",
  seoDescription: "Find what is holding your operation back and where AI or automation can help. Get the investigation, design and implementation support to improve the result.",
  hero: {
    headline: "From business problem to measured result.",
    subheading: "Find what is consuming capacity, decide what needs to change and get the support to make it work. Start with the question your organisation still needs to resolve.",
    image: "/assets/images/services-hero.jpg",
    imagePosition: "center",
    ctas: [
      { label: "Discuss what you want to improve", target: "bookCall", style: "primary" },
      { label: "Find your starting point", target: "/contact/#contact-form", style: "secondary" },
    ],
  },
  sections: [
    {
      heading: "Start where you need help",
      layout: "lead",
      paragraphs: [
        "You may need to establish the cause of a problem, test an existing recommendation or carry an agreed change into use. We review credible work already completed and agree the scope, responsibilities and deliverables before starting. Each stage gives you an outcome you can use independently, with your own team, another provider or LevelUp360.",
      ],
      textLink: { label: "Explore funding", target: "/services/#funding" },
    },
    {
      anchors: ["aidecision", "decision-sprint"],
      stage: "Stage 1",
      tone: "mist",
      layout: "decision",
      heading: "Decide what to change and where to start",
      problem: "You need to handle more work, reduce cost or improve service, but it is unclear what is causing the constraint or which proposal deserves investment.",
      help: "We work with leaders and the people closest to the work to understand the pressure, examine the available evidence and compare practical options. We identify the questions that could change the decision and prepare recommendations with their reasons and tradeoffs. If AI is already under consideration, the Strategic AI Accelerator remains a focused way to examine that investment; the wider service starts with the business problem.",
      outcome: "You receive clear priorities, options and a recommended next step, including what should proceed, change, wait or stop. The findings identify expected benefits, assumptions, missing information and any further investigation needed before design or implementation. A proposed implementation is scoped and priced after enough is known to define it.",
      ctas: [
        { label: "Explore the Accelerator", target: "decisionAccelerator", style: "primary" },
        { label: "Discuss the decision", target: "bookCall", style: "outline" },
      ],
    },
    {
      anchors: ["vantage", "prioritisation"],
      stage: "Stage 2",
      tone: "light",
      layout: "decision",
      signals: ["Current work", "Decisions and exceptions", "Future workflow", "Implementation brief"],
      heading: "Investigate and redesign the selected work",
      problem: "An opportunity has been selected, but the current description may miss important workarounds, exceptions and decisions. Changing the process without understanding them can preserve the problem or create another one.",
      help: "We investigate the selected area to establish how the work actually happens and test the assumptions behind the proposal. Where the findings justify change, we separately scope the redesign: removing unnecessary work, clarifying responsibilities and deciding what people and systems should do. The answer may use your existing technology, integration, automation, AI or a combination.",
      outcome: "The investigation gives you a clear account of the current work, its material constraints and baseline measures, with a recommendation on what to do next. If redesign proceeds, you receive an agreed future workflow covering responsibilities, decisions, exceptions and the changes needed to put it into practice.",
      ctas: [{ label: "Discuss the selected work", target: "bookCall", style: "primary" }],
    },
    {
      anchors: ["aicontrol", "ai-control-pack"],
      stage: "Stage 3",
      tone: "ink",
      layout: "decision",
      signals: ["Solution", "Checks", "Ownership", "Acceptance"],
      heading: "Design the solution and plan implementation",
      problem: "The intended improvement is clear, but the organisation still needs a design it can approve and a delivery team can act on. Requirements, system dependencies or responsibilities may remain unresolved.",
      help: "We turn the agreed change into a practical solution design covering systems, information, integrations, access and operating responsibilities. Where AI is involved, we define what it may do, when people must review or intervene, how errors will be handled and what testing must demonstrate before release. We identify what must be resolved before delivery and agree how the organisation will decide whether the result is ready to use.",
      outcome: "You receive a design and implementation plan explaining what must change, why, who is responsible and how it will be tested. Business owners, technical reviewers and the delivery team have a clear basis for approval, pricing and implementation.",
      ctas: [{ label: "Discuss the implementation plan", target: "bookCall", style: "primary" }],
    },
    {
      anchors: ["delivery", "roadtoprod"],
      stage: "Stage 4",
      tone: "light",
      layout: "decision",
      signals: ["Agreed result", "Implementation", "Handover", "Measurement"],
      heading: "Deliver and measure the change",
      problem: "A design only creates value when the change works in practice. Implementation needs clear responsibility, staff need to be ready to use it and someone must establish whether the intended improvement happened.",
      help: "We carry or coordinate the agreed change through implementation, testing and initial support. Before delivery starts, we agree what LevelUp360 will deliver, what your team or an appointed specialist will deliver, and who approves, operates and supports the result. We prepare handover and compare the agreed measures with the pre-change baseline over the review period.",
      outcome: "You receive the agreed working change or delivery-oversight outputs, a usable handover and clear operating responsibilities. The result is reviewed against the starting point and expected improvement, with an agreed next action where further work is needed.",
      ctas: [{ label: "Discuss implementation and results", target: "bookCall", style: "primary" }],
    },
    {
      heading: "Keep the work connected",
      tone: "ink",
      layout: "continuity",
      flow: ["Problem", "Decision", "Design", "Delivery", "Result", "Next step"],
      paragraphs: [
        "The work should move forward without your team having to explain the same problem again at every handoff. We keep the agreed result, decisions and responsibilities clear across the stages in scope.",
      ],
      ctas: [{ label: "About Govern360", target: "/products/#govern360", style: "primary" }],
    },
    {
      anchors: ["funding"],
      heading: "Funding for eligible Irish businesses",
      tone: "mist",
      layout: "funding",
      options: [
        {
          heading: "Digital assessment and planning",
          body: "Enterprise Ireland's Digital Discovery support can help eligible businesses access expert advice on digital foundations or AI. The published support offers up to 80% funding, to a maximum grant of EUR 5,000.",
          cta: { label: "Check Digital Discovery criteria", target: "enterpriseIrelandDigitalDiscovery" },
        },
        {
          heading: "Business-process redesign and implementation",
          body: "Enterprise Ireland's Digital Process Innovation support offers eligible projects up to 50% funding towards introducing new or significantly improved production or delivery methods, including internal implementation work and external expertise.",
          cta: { label: "Explore Digital Process Innovation", target: "enterpriseIrelandDigitalProcessInnovation" },
        },
      ],
      qualification: "Eligibility, eligible costs and approval depend on the programme and proposed project. Confirm the terms and any required approval with Enterprise Ireland before committing to work. Funding is not guaranteed.",
    },
    {
      heading: "Is the problem worth addressing?",
      layout: "cta",
      paragraphs: [
        "The work is most useful when the cost, delay, service pressure or dependence on key people matters enough to act. You need an accountable sponsor, access to the relevant people and information, and the ability to make the resulting decision. Company size and interest in a particular technology are not the deciding factors. We agree a scope proportionate to the problem and the value of resolving it.",
      ],
      ctas: [{ label: "Tell us what you want to improve", target: "/contact/#contact-form", style: "primary" }],
    },
  ],
};

export const productsPage = {
  variant: "govern360",
  path: "/products/",
  seoTitle: "Govern360 | A LevelUp360 Product In Development",
  seoDescription: "Govern360 is a product currently in development at LevelUp360.",
  hero: {
    eyebrow: "In development",
    anchorId: "govern360",
    headline: "Govern360",
    subheading: "Govern360 is a product currently in development at LevelUp360. For enquiries, contact us.",
    image: "/assets/images/govern360-hero.png",
    imagePosition: "center",
    ctas: [
      { label: "LevelUp360 services", target: "/services/", style: "primary" },
      { label: "Book a call", target: "bookCall", style: "secondary" },
    ],
  },
  sections: [],
};

export const aboutPage = {
  variant: "about",
  path: "/about/",
  seoTitle: "About LevelUp360 | Business Change From Problem To Result",
  seoDescription: "Led by Manuel Tomas Estarlich, LevelUp360 brings more than 20 years of business and technology delivery experience to improving back-office operations.",
  hero: {
    headline: "Experienced help, from business problem to measured result.",
    subheading: "We connect the business, people and technology decisions needed to release capacity, reduce cost or improve service, then help carry the agreed change into use.",
    image: "/assets/images/about-hero.jpg",
    imagePosition: "center",
    ctas: [{ label: "Discuss what you want to improve", target: "bookCall", style: "primary" }],
  },
  sections: [
    {
      heading: "Why LevelUp360 Exists",
      layout: "lead",
      paragraphs: [
        "Businesses need to grow, control cost and improve service, but adding another system does not necessarily remove the work holding them back. LevelUp360 starts by establishing the problem and its cause, then helps the organisation choose and implement the simplest effective response. We bring experienced judgement across business operations and technology, keep the consequential decisions with the customer and measure whether the change delivered the intended result.",
      ],
    },
    {
      heading: "The Work We Are Built For",
      tone: "mist",
      layout: "lead",
      paragraphs: [
        "Our focus is information-heavy back-office work: finance, administration, compliance, order processing and internal services. We help where delays, repeated work, system gaps or dependence on experienced people have a material effect on capacity, cost or service. The answer often crosses team and system boundaries. The scope reflects the problem, your internal capability and the checks needed to make the change work.",
      ],
    },
    {
      heading: "How We Work",
      tone: "ink",
      layout: "process",
      intro: "Problem first, technology last. We look at what needs to improve and what your organisation can realistically implement and maintain.",
      numbered: [
        "Agree the result that matters and who owns it.",
        "Establish how the relevant work happens and what is blocking the result.",
        "Compare practical options and prepare a recommendation for your decision.",
        "Design the agreed change and define its delivery responsibilities and checks.",
        "Carry or coordinate implementation, prepare handover and measure the result.",
      ],
      closing: "The solution may involve a simpler process, better use of existing systems, integration, automation or AI. Each stage has an agreed scope and an outcome you can use independently.",
    },
    {
      heading: "Founder And Delivery Experience",
      tone: "light",
      layout: "evidence",
      paragraphs: [
        "LevelUp360 is led by Manuel Tomas Estarlich, a principal architect with more than 20 years of experience improving business operations through process design, automation and technology. His career spans hands-on software development, commercial solution design, enterprise architecture and delivery leadership across financial services, public services and industry.",
        "He has built business applications, helped build engineering teams and their delivery practices from the ground up, and guided major programmes through design, security review and live operation. His work has required him to bring business owners, senior leaders, engineers, suppliers and security teams together, challenge proposals that would not work in practice, and keep delivery moving across organisational boundaries.",
        "That breadth matters when improving your operation requires several things to change together. Manuel can work through the business problem, test the technical and commercial assumptions, and carry the agreed direction into implementation without losing sight of the people who will use and support it. His AI expertise builds on that wider experience in automation and delivery; it helps you judge where AI adds value and where a simpler change is the better investment.",
      ],
    },
    {
      heading: "What you can expect from us",
      tone: "mist",
      layout: "boundary",
      bullets: [
        "Start with the business result and establish the cause.",
        "Compare practical options before recommending a technology.",
        "Keep recommendations, implemented changes and measured results distinct.",
        "Keep consequential decisions with the accountable people.",
        "Make assumptions and unresolved questions visible.",
        "Agree a proportionate scope, clear responsibilities and a useful outcome.",
      ],
    },
    {
      heading: "Discuss what you want to improve",
      tone: "ink",
      layout: "cta",
      paragraphs: ["Tell us what your organisation needs to achieve and what is making it difficult. We can start with the problem, an existing proposal or an initiative already under way."],
      ctas: [{ label: "Book a call", target: "bookCall", style: "primary" }],
    },
  ],
};

export const researchPage = {
  path: "/research/",
  seoTitle: "Research & Resources | LevelUp360",
  seoDescription: "Applied research and technical resources on dependable business technology, including AI security, architecture and evidence of how systems behave.",
  hero: {
    headline: "Research & Resources",
    subheading: "Applied research and technical resources for people designing, reviewing and operating business systems. The current collection focuses on AI security and architecture.",
    image: "/assets/images/research-hero.png",
    imagePosition: "center",
    ctas: [{ label: "Read the featured working paper", target: "actionIntegrityPaper", style: "primary" }],
  },
  featured: {
    heading: "Featured Applied Research",
    title: "From Layered Controls To Action Integrity",
    author: "Manuel Tomas Estarlich",
    date: "Applied technical working paper, August 2026",
    paragraphs: [
      "Research conducted and authored by Manuel Tomas Estarlich, examining what technical controls can establish and where further evidence is needed before a system can be relied on.",
      "The paper examines an implemented multi-agent system and whether an authorised action remains consistent through execution and verification of the outcome. It identifies a gap it calls stateful action integrity and proposes a bounded experiment; that proposed intervention has not been tested.",
    ],
    status: "Status: author-produced working paper; not peer reviewed or independently validated.",
    primary: { label: "Read The Working Paper", target: "actionIntegrityPaper" },
    links: [
      { label: "Public Evidence Package", target: "actionIntegrityEvidence" },
      { label: "Architecture Companions", target: "actionIntegrityArchitecture" },
      { label: "Release Manifest", target: "actionIntegrityManifest" },
      { label: "GitHub Repository", target: "actionIntegrityRepo" },
    ],
  },
  resources: [
    {
      title: "Agentic AI Examples",
      repository: "levelup360pro / agentic-ai-examples",
      repoTarget: "agenticExamplesRepo",
      body: "Reference architecture for AI workflows on Azure, showing access controls, approval points and records of system activity.",
      cta: { label: "View architecture and code", target: "agenticExamplesArchitecture" },
    },
    {
      title: "Enterprise AI Platform Reference Architecture",
      repository: "levelup360pro / enterprise-ai-platform-reference-architecture",
      repoTarget: "enterpriseArchitectureRepo",
      body: "Reference architecture for AI systems in regulated, document-intensive environments, with design decisions, rationale and implementation guidance.",
      cta: { label: "View reference architecture", target: "enterpriseArchitectureRepo" },
    },
  ],
  principles: "We distinguish what was investigated, what the evidence shows and what remains untested. We publish limitations and incomplete or negative findings alongside results. Where source material is private, we state that boundary rather than imply independent verification.",
};

export const contactPage = {
  path: "/contact/",
  seoTitle: "Contact LevelUp360 | Discuss What You Want To Improve",
  seoDescription: "Tell us where capacity, cost or service is under pressure. Discuss the problem, an existing proposal or a change your team needs help delivering.",
  hero: {
    headline: "What does your business need to do better?",
    subheading: "Tell us where capacity, cost or service is under pressure and what you need to achieve. You do not need a chosen technology or a fully defined solution.",
    image: "/assets/images/contact-hero.jpg",
    imagePosition: "center",
    ctas: [
      { label: "Book a call", target: "bookCall", style: "primary" },
      { label: "Send a message", target: "#contact-form", style: "secondary" },
    ],
  },
  introduction: {
    heading: "Get In Touch",
    paragraphs: [
      "You will speak directly with Manuel Tomas Estarlich. The first conversation is to understand the result you need, what is making it difficult and whether LevelUp360 can help.",
      "We are most useful when an important back-office operation is constrained by delays, repeated work, disconnected systems or dependence on experienced people. You may be deciding where to start, assessing a proposal or seeking help with implementation.",
    ],
  },
  formIntro: "Do not include confidential, personal, regulated or commercially sensitive detail at this stage. We only need enough context to understand whether a conversation is useful. Read the Privacy Policy and Terms And Conditions Of Use before submitting.",
  interestOptions: [
    "Finding what is holding the business back and where to start",
    "Improving a selected process or workflow",
    "Designing a solution and planning implementation",
    "Implementing and measuring an agreed change",
    "Architecture and delivery oversight",
    "Following up on a workshop or existing engagement",
  ],
  consent: "I consent to LevelUp360 storing and processing my submitted information so it can respond to this enquiry. I have read the Privacy Policy.",
  success: "Thank you. Your message has been received. We will review the context and respond if a conversation is likely to be useful.",
  failure: "The message could not be sent. Please try again or use the Book A Call link.",
  faqs: [
    {
      question: "Is this only for large enterprises?",
      answer: "No. Fit depends on the importance of the problem, the value of improving it and your organisation's ability to act. Smaller businesses can face the same capacity constraints and dependence on key people. The scope should remain proportionate.",
    },
    {
      question: "Do you only work on AI?",
      answer: "No. We start with the business result and establish what is preventing it. The answer may be a simpler process, better use of existing systems, integration, automation, AI or a combination. AI must earn its place.",
    },
    {
      question: "What is Govern360?",
      answer: "Govern360 is a product currently in development at LevelUp360. For enquiries, contact us.",
    },
    {
      question: "Can you work with our existing implementation provider?",
      answer: "Yes. We can work with your internal team or appointed provider. We agree who designs, builds, approves, supports and measures the change, so responsibility is clear before delivery starts.",
    },
    {
      question: "Do you replace legal, compliance, security or data protection teams?",
      answer: "No. We involve the relevant specialists and prepare work they can review and challenge. Their decisions and accountability remain theirs.",
    },
  ],
  formEyebrow: "Start with what you need to improve",
  contextLabel: "What would you like to improve, what is happening today and how is it affecting the business?",
};

export const legalPages = {
  privacy: {
    path: "/privacy-policy/",
    seoTitle: "Privacy Policy | LevelUp360",
    seoDescription: "How LevelUp360 Vantage Limited handles personal data across its websites, initiatives, enquiries and business relationships.",
    headline: "Privacy Policy",
    subheading: "How LevelUp360 Vantage Limited handles personal information across its websites, initiatives and business relationships.",
    image: "/assets/images/legal-hero.png",
    updated: "Last updated 31 August 2026",
    introduction: [
      "LevelUp360 is operated by LevelUp360 Vantage Limited. This policy explains how we handle personal data through levelup360.pro, strategicai.pro, the Strategic AI Accelerator and other LevelUp360 initiatives, as well as during enquiries and business relationships.",
      "Strategic AI is a LevelUp360 front end for the Accelerator and other initiatives. It is not a separate legal entity or data controller. References to LevelUp360, we, us or our in this policy mean LevelUp360 Vantage Limited.",
      "Additional information may be provided when you complete a particular application, join an initiative or enter a client engagement. Those notices and agreements supplement this policy for that activity.",
    ],
    sections: [
      {
        heading: "Information We Collect",
        paragraphs: ["Depending on how you interact with LevelUp360, we may collect:"],
        bullets: [
          "your name, business contact details, employer, role and professional profile information;",
          "the business challenge, workflow, opportunity, initiative or service that you ask us to consider;",
          "information supplied through an enquiry, expression of interest, application, meeting, proposal or client engagement;",
          "correspondence, meeting records and administrative information needed to manage a business relationship; and",
          "limited technical information processed when you use our websites, such as an IP address, request time, browser information and requested page.",
        ],
        closing: "Please provide only information that is relevant to the interaction. Do not submit unnecessary confidential, special-category or sensitive operational information through a public form.",
      },
      {
        heading: "How And Why We Use Personal Data",
        paragraphs: [
          "We use personal data to respond to enquiries, arrange conversations, assess expressions of interest or applications, prepare proposals, deliver agreed work, manage relationships and maintain necessary business records.",
          "We use limited technical information to deliver, secure and diagnose our websites, prevent abuse and protect our systems. We do not sell personal data or use contact and application information for unrelated advertising.",
          "Our legal basis depends on the interaction. It may be steps you ask us to take before entering a contract, performance of a contract, our legitimate interests in operating LevelUp360 and responding to relevant business enquiries, your consent, or compliance with a legal obligation.",
        ],
      },
      {
        heading: "Website Forms And Initiatives",
        paragraphs: [
          "Contact submissions made through levelup360.pro are security-checked and delivered to LevelUp360 by email. The website does not write those submissions to a website contact database, although the resulting correspondence may be retained in our email and business records.",
          "If an expression-of-interest or application form is enabled on strategicai.pro, the form will explain what information is required for that initiative and any additional handling that applies before submission. An application does not create a client relationship.",
          "Our websites do not currently provide public user accounts, online checkout, comments or community features. If that changes, this policy and the information shown at collection will be updated first.",
        ],
      },
      {
        heading: "Sharing And Service Providers",
        paragraphs: [
          "We share personal data only when needed to operate LevelUp360, respond to you, assess or deliver an initiative, provide agreed services, obtain professional advice, comply with law or protect legitimate rights.",
          "Hosting, security, email, calendar, collaboration and other service providers may process limited personal data on our behalf under their service and data-protection terms. We may also share relevant information with authorised delivery collaborators where this is necessary and appropriately controlled.",
          "Some providers may process information outside the European Economic Area. Where applicable, we use the transfer arrangements and safeguards required by data-protection law.",
        ],
      },
      {
        heading: "Retention And Security",
        paragraphs: [
          "We keep personal data only for as long as needed for the purpose for which it was collected, to manage a resulting business relationship, to meet contractual or legal obligations, and to establish or defend legal rights. The appropriate period depends on whether the interaction remains an enquiry, becomes an application or forms part of a client engagement.",
          "Information forming part of a client engagement is also governed by the relevant client agreement, delivery controls and applicable record-keeping requirements. Consent-based communications are retained until consent is withdrawn or the information is no longer needed.",
          "We use proportionate technical and organisational safeguards. No internet transmission or storage method can be guaranteed completely secure.",
        ],
      },
      {
        heading: "Cookies, Security And External Services",
        paragraphs: [
          "Our current websites do not use advertising cookies, behavioural analytics pixels or session-replay tools. Essential technical storage may be used only where needed for security or basic operation.",
          "The levelup360.pro contact form uses Cloudflare Turnstile to distinguish legitimate submissions from automated abuse. Cloudflare may process limited device, browser, network and interaction data for that security purpose under its own privacy terms.",
          "Booking, newsletter, LinkedIn, GitHub, research and other external links open services operated by other organisations. Those services process information under their own privacy terms when you choose to use them; they are linked rather than embedded unless clearly stated otherwise.",
        ],
      },
      {
        heading: "Your Rights",
        paragraphs: [
          "Subject to applicable data-protection law, you may ask for access to or a copy of your personal data, correction, deletion, restriction, portability, or object to certain processing. You may withdraw consent at any time without affecting processing that was lawful before withdrawal.",
          "You may also raise a concern with Ireland's Data Protection Commission or another competent supervisory authority. We may need to verify your identity before acting on a request.",
        ],
      },
      {
        heading: "Children And Policy Changes",
        paragraphs: [
          "LevelUp360's websites, initiatives and business services are not directed to children, and we do not knowingly collect children's personal data through them.",
          "We may update this policy when our websites, initiatives, services or data handling change. The date above identifies the current version.",
        ],
      },
    ],
    contact: { label: "Contact LevelUp360", target: "/contact/#contact-form" },
  },
  terms: {
    path: "/terms-conditions-of-use/",
    seoTitle: "Terms And Conditions Of Use | LevelUp360",
    seoDescription: "The terms that apply to LevelUp360-operated websites, initiatives and public resources.",
    headline: "Terms And Conditions Of Use",
    subheading: "The terms that apply when using LevelUp360-operated websites, initiatives and public resources.",
    image: "/assets/images/legal-hero.png",
    updated: "Last updated 31 August 2026",
    introduction: [
      "These terms apply to levelup360.pro, strategicai.pro and other public websites or initiative pages operated by LevelUp360 Vantage Limited. Strategic AI is a LevelUp360 front end for the Accelerator and other initiatives; it is not a separate legal entity.",
      "By using these websites or their public resources, you agree to these terms. If you do not agree, please stop using them. A LevelUp360 client engagement is governed by its own proposal, statement of work or client agreement.",
    ],
    sections: [
      {
        heading: "Websites And Initiatives",
        paragraphs: [
          "Our websites describe LevelUp360 services, Govern360's role in delivery, Strategic AI initiatives, applied research and ways to make contact or register interest. They do not currently provide public user accounts, online checkout, community functions or a self-service software product.",
          "Submitting an enquiry, booking a call, registering interest or making an application does not create a client relationship, guarantee acceptance into an initiative, commit LevelUp360 to provide services or form a contract.",
        ],
      },
      {
        heading: "Services And Engagements",
        paragraphs: [
          "Any engagement, programme place, scope, fees, responsibilities, confidentiality arrangements, deliverables and acceptance terms must be agreed separately in the relevant proposal, statement of work, client agreement or initiative terms.",
          "If a conflict arises between these public website terms and a signed agreement for LevelUp360 services, the signed agreement governs that engagement to the extent of the conflict.",
        ],
      },
      {
        heading: "Information, Research And Outcomes",
        paragraphs: [
          "Website content, initiative descriptions and public resources are general information, not legal, regulatory, financial, security or other professional advice for your circumstances. Applied research is presented with its stated evidence and validation status.",
          "We aim to keep public information accurate, but it may become incomplete or out of date. We do not guarantee a particular business, technical, programme or regulatory outcome from using a website or public resource.",
        ],
      },
      {
        heading: "Intellectual Property",
        paragraphs: [
          "Unless otherwise identified, our websites' copy, visual design, brand assets and original resources belong to LevelUp360 Vantage Limited or are used with permission and are protected by applicable intellectual-property law.",
          "You may view and share links to public pages for legitimate personal or business reference. You may not reproduce, adapt, sell, republish or commercially exploit substantial content, remove rights notices, or imply endorsement without prior written permission. Separate licences shown on linked repositories or resources continue to apply to those materials.",
        ],
      },
      {
        heading: "Acceptable Use",
        paragraphs: ["You must not misuse our websites, forms or public resources or interfere with their operation. In particular, you must not:"],
        bullets: [
          "attempt unauthorised access to a website, server or connected system;",
          "introduce malicious code, overload the service or bypass security controls;",
          "submit unlawful, misleading, abusive or rights-infringing material; or",
          "use automated collection in a way that disrupts the service or violates applicable law.",
        ],
      },
      {
        heading: "External Links",
        paragraphs: [
          "Our websites link to external booking, newsletter, social, code-hosting and research services for convenience. LevelUp360 Vantage Limited does not control their availability, content, security or privacy practices, and a link does not by itself amount to endorsement.",
          "When you leave a LevelUp360-operated website, the external provider's terms and privacy policy apply.",
        ],
      },
      {
        heading: "Availability And Liability",
        paragraphs: [
          "We may correct, change, suspend or withdraw website, initiative or public-resource content or functionality. We do not promise uninterrupted or error-free availability.",
          "To the extent permitted by law, LevelUp360 Vantage Limited is not liable for indirect or consequential loss arising solely from use of, inability to use, or reliance on a public website or resource. Nothing in these terms excludes or limits liability that cannot lawfully be excluded, or affects rights that applicable law gives you.",
        ],
      },
      {
        heading: "Privacy",
        paragraphs: ["Our Privacy Policy explains how LevelUp360 Vantage Limited handles personal data across its websites, initiatives, enquiries and business relationships."],
        link: { label: "Read The Privacy Policy", target: "/privacy-policy/" },
      },
      {
        heading: "Governing Law And Changes",
        paragraphs: [
          "These terms are governed by the laws of Ireland. Subject to any mandatory rights that apply, the Irish courts have jurisdiction over disputes relating to our public websites and resources.",
          "We may update these terms when our websites, initiatives or applicable requirements change. The date above identifies the current version. If a provision is found unenforceable, the remaining provisions continue to apply.",
        ],
      },
    ],
    contact: { label: "Contact LevelUp360", target: "/contact/#contact-form" },
  },
};

export const notFoundPage = {
  seoTitle: "Page Not Found | LevelUp360",
  headline: "This Page Is Not Part Of The Current Journey.",
  body: "The link may be old or the page may have moved. Start again or go directly to the complete LevelUp360 service journey.",
  ctas: [
    { label: "Go To The Homepage", target: "/", style: "primary" },
    { label: "See The Services Journey", target: "/services/", style: "outline" },
  ],
};
